import { useMutation } from "react-query"
import { notification } from "antd"
import { IQuoteClient } from "contexts/opus/context"

type ValidationErrorResponse = {
  response?: { data?: { validationErrors?: { message: string }[] } }
}

export const FLEX_BROKER_COMMISSION_FALLBACK_ERROR =
  "Unable to update broker commission. Please try again."

/**
 * Saves a broker commission flex for a quote. On success, onFlexed is called so the
 * caller can refresh anything that reflects the new commission (premium, documents,
 * timeline). On failure the API's validation messages are surfaced and the error is
 * rethrown, so the adjuster stays open for the broker to correct the value.
 */
export const useFlexBrokerCommission = (
  quoteClient: Pick<IQuoteClient, "setBrokerCommission">,
  quoteId: string,
  onFlexed: () => void
): IUseFlexBrokerCommission => {
  const flexBrokerCommission = useMutation((commissionPercent: number) =>
    quoteClient.setBrokerCommission(quoteId, commissionPercent)
  )

  const handleFlexBrokerCommission = async (commissionPercent: number) => {
    try {
      await flexBrokerCommission.mutateAsync(commissionPercent)
      onFlexed()
    } catch (error) {
      const validationErrors = (error as ValidationErrorResponse)?.response
        ?.data?.validationErrors

      notification.error({
        message:
          validationErrors?.map((e) => e.message).join(" ") ||
          FLEX_BROKER_COMMISSION_FALLBACK_ERROR
      })

      throw error
    }
  }

  return {
    handleFlexBrokerCommission,
    isFlexingBrokerCommission: flexBrokerCommission.isLoading
  }
}

export interface IUseFlexBrokerCommission {
  handleFlexBrokerCommission: (commissionPercent: number) => Promise<void>
  isFlexingBrokerCommission: boolean
}
